import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet } from '../components/Sheet.jsx';
import { montagVon, useMeineStunden, useWocheEinreichen, useWocheZuruecksetzen } from '../hooks/data.js';
import { eingereichtAm, eingereichtMerken, eingereichtVergessen, istGesperrt } from '../lib/wochen.js';
import { newKey } from '../lib/outbox.js';
import { useToast } from '../state/toast.jsx';
import { addDays, hours, isoDate } from '../lib/format.js';
import { Icon } from '../components/Icon.jsx';
import { Stand } from '../components/Stand.jsx';

const TAG = new Intl.DateTimeFormat('de-CH', { weekday: 'short', day: 'numeric', month: 'numeric' });
const tagName = (iso) => TAG.format(new Date(`${iso}T12:00:00`));

/** Meine Stunden pro Tag (alle Aufträge), Woche vor/zurück. */
export function StundenSheet({ onClose }) {
  const [datum, setDatum] = useState(isoDate());
  const [offen, setOffen] = useState(isoDate());
  const woche = useMeineStunden(datum);
  const w = woche.data;
  const heuteWoche = montagVon(datum) >= montagVon(isoDate());

  return (
    <Sheet title="Meine Stunden" onClose={onClose}>
      <div className="between">
        <button className="iconbtn" aria-label="Woche zurück" onClick={() => setDatum(addDays(datum, -7))}><Icon name="back" size={18} /></button>
        <span className="stack" style={{ alignItems: 'center', gap: 0 }}>
          <span style={{ fontWeight: 800 }}>{w?.kw ? `KW ${w.kw}` : 'Woche'} · {w ? hours(w.total) : '–'}</span>
          {w?.tage && <span className="sub" style={{ fontSize: 12 }}>{tagName(w.tage[0].datum)} – {tagName(w.tage[6].datum)}</span>}
        </span>
        <button className="iconbtn" aria-label="Woche vor" onClick={() => setDatum(addDays(datum, 7))} disabled={heuteWoche}><Icon name="chevron" size={18} /></button>
      </div>
      <Stand abfragen={[woche]} />
      {woche.isLoading && <div className="skeleton" style={{ height: 240 }} />}
      {woche.isError && !w && <div className="empty" role="alert">{woche.error.message}<button className="btn" onClick={() => woche.refetch()}>Erneut versuchen</button></div>}
      {w?.tage && (
        <div className="card list" style={{ padding: '4px 14px' }}>
          {w.tage.map((t) => (
            <div key={t.datum} style={{ display: 'flex', flexDirection: 'column' }}>
              <button className="listrow" style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid var(--line-2)', font: 'inherit', color: 'inherit', cursor: 'pointer', flexDirection: 'row' }} onClick={() => setOffen(offen === t.datum ? null : t.datum)} aria-expanded={offen === t.datum}>
                <span style={{ flex: 1, fontWeight: t.datum === isoDate() ? 800 : 600 }}>{t.datum === isoDate() ? 'Heute' : tagName(t.datum)}</span>
                {t.abwesend > 0 && <span className="pill">abwesend {hours(t.abwesend)}</span>}
                <span className="tn" style={{ fontWeight: 800, minWidth: 64, textAlign: 'right', whiteSpace: 'nowrap' }}>{t.stunden ? hours(t.stunden) : '–'}</span>
              </button>
              {offen === t.datum && t.eintraege.length > 0 && (
                <div className="stack" style={{ gap: 6, padding: '0 0 10px 12px' }}>
                  {t.eintraege.map((e, i) => (
                    <div key={i} className="row" style={{ fontSize: 14, gap: 10, alignItems: 'flex-start' }}>
                      <span className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
                        {e.nummer ? <Link to={`/auftrag/${encodeURIComponent(e.nummer)}`} onClick={onClose} className="ellipsis" style={{ fontWeight: 700 }}>{e.nummer} · {e.titel}</Link> : <span className="ellipsis" style={{ fontWeight: 700 }}>{e.titel}</span>}
                        {(e.kunde || e.beschreibung) && <span className="sub ellipsis" style={{ fontSize: 12 }}>{[e.kunde, e.beschreibung].filter(Boolean).join(' · ')}</span>}
                      </span>
                      <span className="tn" style={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{hours(e.stunden)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {w?.tage && w.jahr && w.kw && <Einreichen key={`${w.jahr}-${w.kw}`} woche={w} montag={montagVon(datum)} />}
      <p className="sub" style={{ fontSize: 12 }}>Alle Stunden aus Exxas, egal auf welchem Auftrag. Eigene Buchungen aus der App erscheinen sofort.</p>
    </Sheet>
  );
}

const datumZeit = new Intl.DateTimeFormat('de-CH', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Wochenrapport einreichen bzw. zurückziehen (Exxas «Zur Genehmigung senden» / «reset»).
 * Eingereicht = für den Benutzer gesperrt, darum vor dem Einreichen deutlicher Hinweis und zwei Schritte.
 * «Eingereicht» und «genehmigt» kommen aus Exxas (Status der Woche); das Gerät merkt sich nur die Uhrzeit des Einreichens.
 */
function Einreichen({ woche, montag }) {
  const einreichen = useWocheEinreichen();
  const zuruecksetzen = useWocheZuruecksetzen();
  const toast = useToast();
  const [gemerkt, setGemerkt] = useState(() => eingereichtAm(woche));
  const [schritt, setSchritt] = useState(null); // null | 'einreichen' | 'zurueck'
  const [kommentar, setKommentar] = useState('');
  const [key, setKey] = useState(newKey); // ein Schlüssel pro Vorgang: Wiederholung im Funkloch = einmal
  const laeuftNoch = montagVon(isoDate()) === montag;
  const eingereicht = istGesperrt(woche, woche);
  // Exxas meldet die Woche offen (z. B. anderswo zurückgezogen) → gemerkte Einreichung vergessen
  useEffect(() => {
    if (woche.status !== undefined && !woche.eingereicht && gemerkt) { eingereichtVergessen(woche); setGemerkt(null); }
  }, [woche, gemerkt]);
  const busy = einreichen.isPending || zuruecksetzen.isPending;

  if (woche.genehmigt) {
    return <div className="notice info" role="status" style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)' }}><Icon name="check" size={18} stroke={2.6} />KW {woche.kw} ist in Exxas genehmigt.</div>;
  }
  const senden = async () => {
    try {
      const r = await einreichen.mutateAsync({ jahr: woche.jahr, kw: woche.kw, kommentar: kommentar.trim(), key, montag });
      eingereichtMerken(woche, r.eingereichtAm);
      setGemerkt(r.eingereichtAm);
      setSchritt(null);
      setKey(newKey());
      toast({ text: `KW ${woche.kw} zur Genehmigung eingereicht` });
    } catch (err) {
      toast({ text: `Nicht eingereicht: ${err.message}`, error: true, long: true });
    }
  };
  const zurueckziehen = async () => {
    try {
      await zuruecksetzen.mutateAsync({ jahr: woche.jahr, kw: woche.kw, key, montag });
      eingereichtVergessen(woche);
      setGemerkt(null);
      setSchritt(null);
      setKey(newKey());
      toast({ text: `Einreichung KW ${woche.kw} zurückgezogen, die Woche ist wieder offen` });
    } catch (err) {
      toast({ text: `Nicht zurückgezogen: ${err.message}`, error: true, long: true });
    }
  };

  return (
    <section className="card" aria-labelledby="einreichen-titel" style={{ gap: 12 }}>
      <h3 id="einreichen-titel" className="h2">Wochenrapport KW {woche.kw}</h3>
      {eingereicht ? (
        <>
          <span className="pill info" style={{ alignSelf: 'flex-start' }}>
            <Icon name="lock" size={12} stroke={2.6} />
            {gemerkt ? `Eingereicht ${datumZeit.format(new Date(gemerkt))}, gesperrt` : 'Eingereicht, gesperrt'}
          </span>
          {schritt !== 'zurueck' ? (
            <>
              <span className="sub">Zeit erfassen oder ändern geht in dieser Woche erst wieder, wenn du die Einreichung zurückziehst oder das Büro sie zurückweist.</span>
              <button className="btn secondary lg block" onClick={() => setSchritt('zurueck')}>Einreichung zurückziehen</button>
            </>
          ) : (
            <>
              <span className="sub">Die Woche ist danach wieder offen. Wenn alles stimmt, reichst du sie neu ein.</span>
              <button className="btn lg block" onClick={zurueckziehen} disabled={busy}>{zuruecksetzen.isPending ? 'Wird zurückgezogen …' : `KW ${woche.kw} wieder öffnen`}</button>
              <button className="btn secondary block" onClick={() => setSchritt(null)} disabled={busy}>Abbrechen</button>
            </>
          )}
        </>
      ) : schritt !== 'einreichen' ? (
        <>
          <span className="sub">Total {hours(woche.total)}.{laeuftNoch ? ' Die Woche läuft noch.' : ''} Nach dem Einreichen prüft das Büro die Woche in Exxas.</span>
          <button className="btn lg block" onClick={() => setSchritt('einreichen')}>KW {woche.kw} einreichen</button>
        </>
      ) : (
        <>
          <div className="notice" role="note"><Icon name="lock" size={18} stroke={2.2} />Danach ist die Woche gesperrt: Zeit erfassen geht erst wieder, wenn du die Einreichung zurückziehst oder das Büro sie zurückweist.</div>
          <label className="field">
            <span className="label">Bemerkung ans Büro (optional)</span>
            <textarea className="input" rows={2} maxLength={500} value={kommentar} onChange={(e) => setKommentar(e.target.value)} placeholder="z. B. Freitag halber Tag Arzt" />
          </label>
          <button className="btn lg block" onClick={senden} disabled={busy}>
            {einreichen.isPending ? 'Wird eingereicht …' : `Jetzt einreichen (${hours(woche.total)})`}
          </button>
          <button className="btn secondary block" onClick={() => setSchritt(null)} disabled={busy}>Abbrechen</button>
        </>
      )}
    </section>
  );
}
